// Generated by GitHub Copilot - Oct-01-2026

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MasterService } from '../../../core/services/master.service';
import { ApplicationLogFieldResponse, AuditFieldChange, AuditLogDetail, AuditStatus } from '../audit-log.interfaces';

/**
 * Read-only Event Detail page for a single audit-log entry.
 * Opened in a new tab from the grid and loaded by Application Log ID.
 */
@Component({
  standalone: true,
  selector: 'app-audit-log-detail',
  imports: [CommonModule, RouterLink],
  templateUrl: './audit-log-detail.component.html',
  styleUrl: './audit-log-detail.component.css',
})
export class AuditLogDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private masterService = inject(MasterService);

  isLoading = false;
  applicationLogId: number | null = null;
  logId = '';
  detail: AuditLogDetail | null = null;

  ngOnInit(): void {
    const routeId = Number(this.route.snapshot.paramMap.get('applicationLogId'));
    this.applicationLogId = Number.isInteger(routeId) && routeId > 0 ? routeId : null;
    this.logId = this.route.snapshot.queryParamMap.get('logId') ?? '';
    this.loadDetail();
  }

  loadDetail(): void {
    if (this.applicationLogId === null) return;
    this.isLoading = true;
    this.masterService
      .getApplicationLogFields(this.applicationLogId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.detail = this.mapDetail(rows ?? []);
          if (this.detail) this.logId = this.detail.logId;
          this.isLoading = false;
        },
        error: () => {
          this.detail = null;
          this.isLoading = false;
        },
      });
  }

  private mapDetail(rows: ApplicationLogFieldResponse[]): AuditLogDetail | null {
    const summary = rows[0];
    if (!summary) return null;

    const changesByField = new Map<string, AuditFieldChange>();
    for (const row of rows) {
      const fieldName = row.FieldName?.trim();
      if (!fieldName || (!('OldValue' in row) && !('NewValue' in row))) continue;
      changesByField.set(fieldName, {
        fieldName,
        oldValue: this.displayValue(row.OldValue),
        newValue: this.displayValue(row.NewValue),
      });
    }

    return {
      logId: summary.LogID,
      dateTime: summary.DateAndTime,
      performedBy: summary.PerformedBy,
      role: summary.Role,
      module: summary.Module,
      actionPerformed: summary.ActionPerformed,
      recordId: String(summary.RecordID ?? ''),
      reviewName: summary.ReviewName,
      status: summary.Status,
      fieldChanges: Array.from(changesByField.values()),
    };
  }

  private displayValue(value: string | null): string {
    if (value === null || value === '') return 'Blank';
    const ratingTitleCase: Record<string, string> = { yellow: 'Yellow', green: 'Green', red: 'Red', blue: 'Blue' };
    return ratingTitleCase[value.toLowerCase()] ?? value;
  }

  /**
   * Breadcrumb handler. This page opens in a new tab from the grid, so closing it
   * returns the user to the previous tab. If the tab can't be closed (opened directly),
   * fall back to the Audit Log tab in this window.
   */
  goBack(event: Event): void {
    event.preventDefault();
    window.close();
    setTimeout(() => {
      if (!window.closed) this.router.navigate(['/admin'], { queryParams: { tab: 'audit' } });
    }, 100);
  }

  /** CSS modifier class for the status badge. */
  statusBadgeClass(status: AuditStatus): string {
    return 'status-' + status.toLowerCase();
  }
}
